
import {
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import CrudListing from "components/crud/CrudListing";
import JourneyPlanModal from "./JourneyPlanModal";

import { journeyPlanColumns } from "./columns";

import {
  getJourneyPlans,
  deleteJourneyPlan,
  submitJourneyPlan,
  approveJourneyPlan,
  rejectJourneyPlan,
  importJourneyPlans,
} from "services/api/journeyPlanApi";

import { getEmployees } from "services/api/employeeApi";

import {
  showSuccess,
  showError,
} from "utils/toast";

// ============================================================
// COMPONENT
// ============================================================

export default function JourneyPlans() {
  // ============================================================
  // LOGGED-IN USER
  // ============================================================

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("user") || "{}"
      );
    } catch (error) {
      console.error(
        "Unable to parse logged-in user.",
        error
      );

      return {};
    }
  }, []);

  // ============================================================
  // ROLE CHECK
  // ============================================================

  const userRoles = useMemo(
    () =>
      Array.isArray(loggedInUser?.roles)
        ? loggedInUser.roles.map((role) =>
            String(role).toLowerCase()
          )
        : [],
    [loggedInUser]
  );

  const isSalesPerson =
    userRoles.includes("sales_person");

  // ============================================================
  // EMPLOYEES
  // ============================================================

  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] =
    useState(false);

  // ============================================================
  // LOAD EMPLOYEES
  // ============================================================

  useEffect(() => {
    if (isSalesPerson) {
      setEmployees([]);
      return undefined;
    }

    let isMounted = true;

    const loadEmployees = async () => {
      try {
        setEmployeeLoading(true);

        const response = await getEmployees(1, 100);

        const employeeData =
          response?.data?.data?.employees ||
          response?.data?.employees ||
          response?.data?.data?.records ||
          response?.data?.records ||
          [];

        if (isMounted) {
          setEmployees(
            Array.isArray(employeeData)
              ? employeeData
              : []
          );
        }
      } catch (error) {
        console.error(
          "Journey Plan employee filter error:",
          error
        );

        if (isMounted) {
          setEmployees([]);

          showError(
            error?.response?.data?.message ||
              "Unable to load employees."
          );
        }
      } finally {
        if (isMounted) {
          setEmployeeLoading(false);
        }
      }
    };

    loadEmployees();

    return () => {
      isMounted = false;
    };
  }, [isSalesPerson]);

  // ============================================================
  // EMPLOYEE FILTER OPTIONS
  // ============================================================

  const employeeOptions = useMemo(() => {
    if (isSalesPerson) {
      const employee = loggedInUser?.employee;

      if (!employee?.id) {
        return [];
      }

      const employeeName =
        loggedInUser?.name ||
        employee?.user?.name ||
        employee?.name ||
        "Current Employee";

      const employeeCode =
        employee?.employeeCode ||
        employee?.code ||
        "";

      return [
        {
          value: String(employee.id),
          label: employeeCode
            ? `${employeeName} (${employeeCode})`
            : employeeName,
        },
      ];
    }

    return [
      {
        value: "",
        label: "All Salespersons",
      },
      ...employees
        .filter((employee) => employee?.id != null)
        .map((employee) => {
          const name =
            employee?.user?.name ||
            employee?.name ||
            "Unknown Employee";

          const code =
            employee?.employeeCode ||
            employee?.code ||
            employee?.employee_code ||
            "";

          return {
            value: String(employee.id),
            label: code
              ? `${name} (${code})`
              : name,
          };
        }),
    ];
  }, [isSalesPerson, loggedInUser, employees]);

  // ============================================================
  // STATUS OPTIONS
  // ============================================================

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "draft", label: "Draft" },
    { value: "submitted", label: "Submitted" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // ============================================================
  // FETCH JOURNEY PLANS
  // ============================================================

  const fetchJourneyPlans = async (
    page,
    limit,
    search,
    filterValues
  ) => {
    console.log(
      "Journey Plan Filters:",
      filterValues
    );

    return getJourneyPlans(
      page,
      limit,
      filterValues || {}
    );
  };

  // ============================================================
  // DOWNLOAD CSV TEMPLATE
  // ============================================================

  const downloadJourneyPlanTemplate = () => {
    const headers = [
      "planDate",
      "title",
      "employeeId",
      "status",
      "schoolId",
      "partyId",
      "plannedTime",
      "purpose",
      "sequence",
      "claimAmount",
    ];

    const exampleRow = [
      "2026-10-10",
      "October Journey Plan",
      "",
      "draft",
      "",
      "",
      "10:00",
      "School visit",
      "1",
      "0",
    ];

    const escapeCsvValue = (value) => {
      const stringValue = String(value ?? "");
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      exampleRow.map(escapeCsvValue).join(","),
    ].join("\r\n");

    const blob = new Blob(
      ["\uFEFF", csvContent],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "journey-plan-template.csv";

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  // ============================================================
  // EXCEL / CSV IMPORT
  // ============================================================

  const fileInputRef = useRef(null);

  const [importLoading, setImportLoading] =
    useState(false);

  const handleExcelImport = async (event) => {
    const input = event.target;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const allowedExtensions = /\.(xlsx|xls|csv)$/i;

    if (!allowedExtensions.test(file.name)) {
      showError(
        "Please select a valid Excel (.xlsx, .xls) or CSV file."
      );

      input.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setImportLoading(true);

      const response =
        await importJourneyPlans(formData);

      showSuccess(
        response?.data?.message ||
          "Journey plans imported successfully."
      );

      // Reload after successful import only.
      window.location.reload();
    } catch (error) {
      console.error(
        "Journey plan import error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to import journey plans."
      );
    } finally {
      setImportLoading(false);

      // Allow the same file to be selected again.
      input.value = "";
    }
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async (id) => {
    try {
      await submitJourneyPlan(id);

      showSuccess("Journey plan submitted.");

      window.location.reload();
    } catch (error) {
      console.error(
        "Submit journey plan error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to submit plan."
      );
    }
  };

  // ============================================================
  // APPROVE
  // ============================================================

  const handleApprove = async (id) => {
    try {
      await approveJourneyPlan(id, {
        remarks: "Approved",
      });

      showSuccess("Journey plan approved.");

      window.location.reload();
    } catch (error) {
      console.error(
        "Approve journey plan error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to approve plan."
      );
    }
  };

  // ============================================================
  // REJECT
  // ============================================================

  const handleReject = async (id) => {
    const reason = window.prompt("Reject reason");

    if (!reason?.trim()) {
      return;
    }

    try {
      await rejectJourneyPlan(id, {
        reason: reason.trim(),
      });

      showSuccess("Journey plan rejected.");

      window.location.reload();
    } catch (error) {
      console.error(
        "Reject journey plan error:",
        error
      );

      showError(
        error?.response?.data?.message ||
          "Unable to reject plan."
      );
    }
  };

  // ============================================================
  // TABLE COLUMNS
  // ============================================================

  const columns = useMemo(
    () =>
      (props) =>
        journeyPlanColumns({
          ...props,
          onSubmit: handleSubmit,
          onApprove: handleApprove,
          onReject: handleReject,
        }),
    []
  );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="mt-5">
      {/* ======================================================
          IMPORT / TEMPLATE ACTIONS
      ====================================================== */}

      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleExcelImport}
          disabled={importLoading}
        />

        <button
          type="button"
          onClick={downloadJourneyPlanTemplate}
          className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
        >
          <span aria-hidden="true">↓</span>
          Download CSV Template
        </button>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={importLoading}
          className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm font-semibold text-green-700 transition hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-green-200"
        >
          {importLoading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
              Importing...
            </>
          ) : (
            <>
              <span aria-hidden="true">↑</span>
              Import Excel / CSV
            </>
          )}
        </button>
      </div>

      {/* ======================================================
          EXISTING LISTING - PRESERVED
      ====================================================== */}

      <CrudListing
        title="Journey Plans"
        columns={columns}
        Modal={JourneyPlanModal}
        fetchData={fetchJourneyPlans}
        deleteData={deleteJourneyPlan}
        responseDataKey="journeyPlans"
        showSearch={false}
        addButtonText="Create Plan"
        limit={10}
        filters={[
          {
            key: "employeeId",
            label: "Salesperson",
            type: "select",
            defaultValue: isSalesPerson
              ? String(
                  loggedInUser?.employee?.id || ""
                )
              : "",
            placeholder: employeeLoading
              ? "Loading salespersons..."
              : "All Salespersons",
            options: employeeOptions,
          },
          {
            key: "status",
            label: "Status",
            type: "select",
            defaultValue: "",
            placeholder: "All Status",
            options: statusOptions,
          },
          {
            key: "planDate",
            label: "Plan Date",
            type: "date",
            defaultValue: "",
          },
        ]}
      />
    </div>
  );
}